'use client';

import { c as _c } from "react/compiler-runtime";
import type { ProductVariant, ShopifyImage as ShopifyProductImage } from '@repo/shopify';
import { cn } from '@repo/ui/lib/utils';
import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { ProductBadgeChip, ProductBadgeStack, resolveProductBadges, salePercentOff } from '@/components/product/product-badges';
import { getProductCardQuickAddData } from '@/components/product/product-card-shared';
import { ShopifyImage } from '@/components/ui/shopify-image';
import { formatMoney } from '@/lib/money';
import { useIdleModule } from '@/lib/use-idle-module';
import { type BundleEntry, useBundle } from './bundle-context';
import type { BundleProduct } from './bundle-types';

// Loaded during idle time so the first tap renders the modal synchronously
// (see use-idle-module.ts); shares its chunk with the product-card quick add.
const loadQuickAddModal = () => import('@/components/product/quick-add-modal').then(m => m.QuickAddModal);
function createBundleEntry({
  product,
  variant,
  image
}: {
  product: BundleProduct;
  variant: ProductVariant;
  image: ShopifyProductImage | null;
}): BundleEntry {
  return {
    variantId: variant.id,
    productId: product.id,
    handle: product.handle,
    title: product.title,
    vendor: product.vendor,
    productType: product.productType,
    variantTitle: variant.title === 'Default Title' ? undefined : variant.title,
    selectedOptions: variant.selectedOptions,
    unitAmount: Number.parseFloat(variant.price.amount),
    currencyCode: variant.price.currencyCode,
    sku: variant.sku,
    image
  };
}
interface BundleProductCardProps {
  product: BundleProduct;
  imageSizes?: string;
}
export function BundleProductCard(t0) {
  const $ = _c(74);
  const {
    product,
    imageSizes: t1
  } = t0;
  const imageSizes = t1 === undefined ? "(min-width: 1280px) 20vw, (min-width: 768px) 25vw, 50vw" : t1;
  const {
    isProductInBundle,
    addVariant,
    removeProduct,
    moneyLocale
  } = useBundle();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMounted, setModalMounted] = useState(false);
  const inBundle = isProductInBundle(product.id);
  let t2;
  if ($[0] !== product) {
    t2 = getProductCardQuickAddData(product);
    $[0] = product;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    primaryImage,
    defaultVariant,
    hasMultipleVariants,
    hasSizeOption
  } = t2;
  const needsSelection = hasMultipleVariants || hasSizeOption;
  const price = product.priceRange.minVariantPrice;
  const compareAtPrice = product.compareAtPriceRange?.minVariantPrice;
  const isOnSale = Boolean(compareAtPrice && Number.parseFloat(compareAtPrice.amount) > Number.parseFloat(price.amount));
  let t3;
  if ($[2] !== compareAtPrice?.amount || $[3] !== isOnSale || $[4] !== price.amount || $[5] !== product.availableForSale || $[6] !== product.tags) {
    t3 = resolveProductBadges({
      tags: product.tags,
      availableForSale: product.availableForSale,
      isOnSale,
      percentOff: salePercentOff(price.amount, compareAtPrice?.amount)
    });
    $[2] = compareAtPrice?.amount;
    $[3] = isOnSale;
    $[4] = price.amount;
    $[5] = product.availableForSale;
    $[6] = product.tags;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    imageBadges,
    titleBadge
  } = t3;
  const soldOut = !product.availableForSale;
  const {
    module: QuickAddModal,
    load: loadModal
  } = useIdleModule(loadQuickAddModal);
  let t4;
  if ($[8] !== loadModal) {
    t4 = () => {
      loadModal();
      setModalMounted(true);
      setModalOpen(true);
    };
    $[8] = loadModal;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const openModal = t4;
  let t5;
  if ($[10] !== addVariant || $[11] !== defaultVariant || $[12] !== primaryImage || $[13] !== product) {
    t5 = () => {
      if (!defaultVariant?.availableForSale) {
        return;
      }
      addVariant(createBundleEntry({
        product,
        variant: defaultVariant,
        image: defaultVariant.image || primaryImage
      }));
    };
    $[10] = addVariant;
    $[11] = defaultVariant;
    $[12] = primaryImage;
    $[13] = product;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const addDefaultVariant = t5;
  const handlePrimary = () => {
    if (inBundle) {
      removeProduct(product.id);
      return;
    }
    if (needsSelection) {
      openModal();
      return;
    }
    addDefaultVariant();
  };
  const t6 = "group flex h-full flex-col";
  const t7 = cn("relative aspect-[3/4] overflow-hidden rounded bg-neutral-100", inBundle && "ring-2 ring-brand-accent ring-offset-2");
  let t8;
  if ($[15] !== imageSizes || $[16] !== primaryImage || $[17] !== product.title) {
    t8 = primaryImage && <ShopifyImage src={primaryImage.url} alt={primaryImage.altText || product.title} fill={true} className="object-cover" sizes={imageSizes} />;
    $[15] = imageSizes;
    $[16] = primaryImage;
    $[17] = product.title;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = `View ${product.title}`;
  let t10;
  if ($[19] !== openModal || $[20] !== t9) {
    t10 = <button type="button" onClick={openModal} aria-label={t9} className="absolute inset-0 z-0 cursor-pointer" />;
    $[19] = openModal;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== imageBadges || $[23] !== inBundle) {
    t11 = !inBundle && <ProductBadgeStack badges={imageBadges} className="pointer-events-none absolute left-3 top-3 flex flex-col items-start gap-1.5" />;
    $[22] = imageBadges;
    $[23] = inBundle;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const t12 = inBundle ? `Remove ${product.title} from bundle` : `Add ${product.title} to bundle`;
  const t13 = inBundle ? "border-brand-accent bg-brand-accent text-white hover:brightness-110" : "border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100";
  let t14;
  if ($[25] !== t13) {
    t14 = cn("absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded border shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60", t13);
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== inBundle) {
    t15 = inBundle ? <X className="h-4 w-4" strokeWidth={3} /> : <Plus className="h-4 w-4" />;
    $[27] = inBundle;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== handlePrimary || $[30] !== soldOut || $[31] !== t12 || $[32] !== t14 || $[33] !== t15) {
    t16 = <button type="button" onClick={handlePrimary} disabled={soldOut} aria-label={t12} className={t14}>{t15}</button>;
    $[29] = handlePrimary;
    $[30] = soldOut;
    $[31] = t12;
    $[32] = t14;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t10 || $[36] !== t11 || $[37] !== t16 || $[38] !== t7 || $[39] !== t8) {
    t17 = <div className={t7}>{t8}{t10}{t11}{t16}</div>;
    $[35] = t10;
    $[36] = t11;
    $[37] = t16;
    $[38] = t7;
    $[39] = t8;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== product.title) {
    t18 = <h3 className="line-clamp-2 text-xs font-semibold uppercase leading-snug text-neutral-900">{product.title}</h3>;
    $[41] = product.title;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== titleBadge) {
    t19 = titleBadge && <div className="mt-2"><ProductBadgeChip badge={titleBadge} /></div>;
    $[43] = titleBadge;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  const t20 = isOnSale ? "text-brand-accent" : "text-neutral-900";
  let t21;
  if ($[45] !== t20) {
    t21 = cn("text-[11px] font-semibold", t20);
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== moneyLocale || $[48] !== price) {
    t22 = formatMoney(price, moneyLocale);
    $[47] = moneyLocale;
    $[48] = price;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t21 || $[51] !== t22) {
    t23 = <span className={t21}>{t22}</span>;
    $[50] = t21;
    $[51] = t22;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== compareAtPrice || $[54] !== isOnSale || $[55] !== moneyLocale) {
    t24 = isOnSale && compareAtPrice && <span className="text-[11px] text-neutral-500 line-through">{formatMoney(compareAtPrice, moneyLocale)}</span>;
    $[53] = compareAtPrice;
    $[54] = isOnSale;
    $[55] = moneyLocale;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== t23 || $[58] !== t24) {
    t25 = <div className="mt-1 flex items-center gap-2">{t23}{t24}</div>;
    $[57] = t23;
    $[58] = t24;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== t18 || $[61] !== t19 || $[62] !== t25) {
    t26 = <div className="mt-3">{t18}{t19}{t25}</div>;
    $[60] = t18;
    $[61] = t19;
    $[62] = t25;
    $[63] = t26;
  } else {
    t26 = $[63];
  }
  let t27;
  if ($[64] !== QuickAddModal || $[65] !== addVariant || $[66] !== modalMounted || $[67] !== modalOpen || $[68] !== product) {
    t27 = modalMounted && QuickAddModal && <QuickAddModal open={modalOpen} onOpenChange={setModalOpen} product={product} addButtonLabel="Add to Bundle" onAddSelectedVariant={t28 => {
      const {
        variant,
        image
      } = t28;
      addVariant(createBundleEntry({
        product,
        variant,
        image
      }));
    }} />;
    $[64] = QuickAddModal;
    $[65] = addVariant;
    $[66] = modalMounted;
    $[67] = modalOpen;
    $[68] = product;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  let t28;
  if ($[70] !== t17 || $[71] !== t26 || $[72] !== t27) {
    t28 = <div className={t6}>{t17}{t26}{t27}</div>;
    $[70] = t17;
    $[71] = t26;
    $[72] = t27;
    $[73] = t28;
  } else {
    t28 = $[73];
  }
  return t28;
}